/**
 * Plan — DESKTOP.
 *
 * Grilla de mes de verdad, que es donde el motor de §4.4 se vuelve legible: se ve de un vistazo
 * dónde están los domingos cerrados, el viaje, los descansos intercalados y qué día de rutina
 * cae en cada fecha. En el celular eso es una lista porque una celda de 45 px no puede decir
 * "Día 2 — hombros, espalda alta y core".
 *
 * A la derecha, el panel de simulación: clic en cualquier día y se ve qué le pasa al colchón.
 */

import { useState, type ReactNode } from 'react';
import { diaSemana, fechaLarga } from '@mg/core';

import { diaMes, queFaltaBloqueo, useCalendario, type DiaCalendario } from '../features/useCalendario.ts';
import { useRuta } from '../ui/router.tsx';
import { Aviso, Campo, Cifra, Insignia, PilaDePlan, Vacio } from '../ui/componentes.tsx';

const DIAS_SEMANA = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

/** Índice de columna con la semana arrancando en lunes. */
const columna = (fecha: string): number => {
  const d = diaSemana(fecha);
  return d === 0 ? 6 : d - 1;
};

export function CalendarioDesktop(): ReactNode {
  const c = useCalendario();
  const { ir } = useRuta();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [motivo, setMotivo] = useState('Viaje');

  if (c.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (!c.calendario || !c.plan) {
    return (
      <section className="tarjeta ancho-forma">
        <h2 className="titulo">Todavía no hay un paquete cargado</h2>
        <p className="nota" style={{ marginTop: 'var(--e2)' }}>
          El plan es el paquete de sesiones que compraste: cuántas son y cuándo vencen. Con eso esta
          pantalla te dice si te alcanza el tiempo y qué pasa si faltás un día.
        </p>
        <button
          type="button"
          className="boton boton--principal"
          style={{ marginTop: 'var(--e4)' }}
          onClick={() => ir('ajustes', 'rutina')}
        >
          Cargar un paquete
        </button>
      </section>
    );
  }

  const primero = c.dias[0];
  const relleno = primero ? columna(primero.fecha) : 0;
  const vencido = c.plan.vencido;
  const faltaBloqueo = queFaltaBloqueo(desde, hasta);

  return (
    <div className="d-rejilla" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', alignItems: 'start' }}>
      <div className="columna" style={{ gap: 'var(--e5)' }}>
        {/* El modelo entero en una línea: sin esto, "COLCHÓN 4" no dice qué vence ni qué son las 12.
            Va en cuerpo de texto y no en `.nota`: es la frase que explica la pantalla entera, y era
            el texto más chico de toda la vista. */}
        <div className="fila fila--entre">
          <p style={{ lineHeight: 1.45 }}>
            Paquete de <span className="lectura">{c.plan.sesionesTotales}</span> sesiones · vence el{' '}
            <span className="lectura">
              {c.datos.planActivo ? diaMes(c.datos.planActivo.fecha_vencimiento) : '—'}
            </span>{' '}
            · te quedan <span className="lectura">{c.plan.restantes}</span>.
          </p>
          {vencido && (
            <button type="button" className="boton boton--chico" onClick={() => ir('ajustes', 'rutina')}>
              Cargar un paquete
            </button>
          )}
        </div>

        <div className="d-rejilla d-rejilla--4">
          <div className="tarjeta">
            <span className="eyebrow">Colchón</span>
            <span
              className="cifra-grande"
              style={{ color: c.calendario.colchonCritico ? 'var(--ambar)' : undefined }}
            >
              {c.calendario.colchon}
            </span>
            <p className="nota--fina">días de margen</p>
            <p className="nota--fina" style={{ marginTop: 4 }}>
              <span className="lectura">{c.calendario.diasHabiles}</span> días hábiles −{' '}
              <span className="lectura">{c.calendario.sesionesPendientes}</span> pendientes
            </p>
          </div>
          <div className="tarjeta">
            <span className="eyebrow">Días hábiles</span>
            <Cifra valor={c.calendario.diasHabiles} decimales={0} />
            <p className="nota--fina">hasta el vencimiento</p>
          </div>
          <div className="tarjeta">
            <span className="eyebrow">Pendientes</span>
            <Cifra valor={c.plan.restantes} decimales={0} />
            <p className="nota--fina">de {c.plan.sesionesTotales}</p>
          </div>
          {/* "EN RIESGO / 0 / van a sobrar" se leía dos veces y ninguna entera. El encabezado dice
              de qué riesgo se habla y el pie sólo cierra la frase. */}
          <div className="tarjeta">
            <span className="eyebrow">En riesgo de vencer</span>
            <div style={{ color: c.calendario.sesionesEnRiesgo > 0 ? 'var(--ladrillo)' : undefined }}>
              <Cifra valor={c.calendario.sesionesEnRiesgo} decimales={0} />
            </div>
            <p className="nota--fina">
              {c.calendario.sesionesEnRiesgo === 0
                ? 'no se te vence ninguna'
                : `van a sobrar ${c.calendario.sesionesEnRiesgo} al vencimiento`}
            </p>
          </div>
        </div>

        <section className="tarjeta">
          <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
            <span className="eyebrow">
              {c.datos.planActivo
                ? `${diaMes(c.datos.planActivo.fecha_inicio)} → ${diaMes(c.datos.planActivo.fecha_vencimiento)}`
                : ''}
            </span>
            <div>
              <PilaDePlan
                usadas={c.plan.usadas}
                pendientes={c.plan.restantes}
                enRiesgo={c.calendario.sesionesEnRiesgo}
                alto={16}
              />
              <p className="nota--fina" style={{ marginTop: 4, textAlign: 'right' }}>
                <span className="lectura">{c.plan.usadas}</span> usadas ·{' '}
                <span className="lectura">{c.plan.restantes}</span> quedan
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
            {DIAS_SEMANA.map((d) => (
              <span key={d} className="eyebrow" style={{ textAlign: 'center', paddingBottom: 4 }}>
                {d}
              </span>
            ))}

            {Array.from({ length: relleno }, (_, i) => (
              <span key={`r-${i}`} />
            ))}

            {c.dias.map((d) => (
              <Celda key={d.fecha} d={d} onSimular={() => c.simular(d.fecha)} />
            ))}
          </div>

          <div className="fila" style={{ gap: 'var(--e4)', marginTop: 'var(--e4)', flexWrap: 'wrap' }}>
            <Leyenda color="var(--ambar)" texto="sesión propuesta" />
            <Leyenda color="var(--agua)" texto="ya hecha" />
            <Leyenda color="var(--hierro-borde)" texto="cerrado o bloqueado" />
            <Leyenda color="transparent" texto="libre" borde />
          </div>
        </section>

        {c.avisos.length > 0 && (
          <div className="columna" style={{ gap: 'var(--e2)' }}>
            {c.avisos.map((a) => (
              <Aviso key={a.clave} tono={a.tono}>
                {a.texto}
              </Aviso>
            ))}
          </div>
        )}

        <section className="tarjeta" style={{ padding: 0 }}>
          <div style={{ padding: 'var(--e4)' }}>
            <span className="eyebrow">Calendario propuesto</span>
          </div>
          <table className="tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Día</th>
                <th>Rutina</th>
                <th>Nota</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {c.calendario.entradas.map((e) => (
                <tr key={e.fecha}>
                  <td className="lectura">{diaMes(e.fecha)}</td>
                  <td className="apagado" style={{ textTransform: 'capitalize' }}>
                    {fechaLarga(e.fecha).split(' ')[0]}
                  </td>
                  <td>{e.nombre}</td>
                  <td>
                    {e.fijada && <Insignia>ya la tenías agendada</Insignia>}
                    {e.conflicto && (
                      <span className="nota--fina" style={{ color: 'var(--ambar)' }}>
                        {e.conflicto.recomendacion}
                      </span>
                    )}
                  </td>
                  <td>
                    <button type="button" className="boton boton--chico boton--fantasma" onClick={() => c.simular(e.fecha)}>
                      ¿Y si falto?
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <aside className="columna" style={{ gap: 'var(--e4)' }}>
        {c.simulando && (
          <Aviso tono="atencion" titulo={`Si faltás el ${fechaLarga(c.simulando.fecha)}`}>
            {c.simulando.cambios.length === 0
              ? 'No cambia nada: te sigue alcanzando.'
              : c.simulando.cambios.map((x) => (
                  <span key={x.texto} style={{ display: 'block' }}>
                    {x.texto}
                  </span>
                ))}
            <div className="fila" style={{ gap: 8, marginTop: 10 }}>
              <button
                type="button"
                className="boton boton--chico"
                onClick={() => void c.bloquear(c.simulando!.fecha, c.simulando!.fecha, 'No pude ir')}
              >
                Bloquearlo
              </button>
              <button type="button" className="boton boton--chico boton--fantasma" onClick={c.limpiarSimulacion}>
                Cerrar
              </button>
            </div>
          </Aviso>
        )}

        {c.ultimosCambios.length > 0 && (
          <Aviso tono="info" titulo="Qué cambió">
            {c.ultimosCambios.map((x) => (
              <span key={x.texto} style={{ display: 'block' }}>
                {x.texto}
              </span>
            ))}
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ marginTop: 8 }}
              onClick={c.limpiarCambios}
            >
              Entendido
            </button>
          </Aviso>
        )}

        <section className="tarjeta">
          <span className="eyebrow">Bloquear días</span>
          <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
            <Campo etiqueta="Desde" texto type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
            <Campo
              etiqueta="Hasta"
              texto
              type="date"
              value={hasta}
              min={desde}
              onChange={(e) => setHasta(e.target.value)}
            />
            <Campo etiqueta="Motivo" texto value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <button
              type="button"
              className="boton boton--principal"
              disabled={faltaBloqueo !== null}
              onClick={() => void c.bloquear(desde, hasta, motivo)}
            >
              Bloquear y recalcular
            </button>
            {/* flujo.md §3: el botón arranca deshabilitado —las dos fechas vienen vacías— y sin
                esta línea es un botón gris que no dice por qué. */}
            {faltaBloqueo && <span className="nota--fina">{faltaBloqueo}</span>}
          </div>
        </section>

        {c.dias.some((d) => d.bloqueado) && (
          <section className="tarjeta">
            <span className="eyebrow">Bloqueados</span>
            <div className="columna" style={{ gap: 6, marginTop: 'var(--e2)' }}>
              {c.dias
                .filter((d) => d.bloqueado)
                .map((d) => (
                  <div key={d.fecha} className="fila fila--entre">
                    <span className="nota">
                      <span className="lectura">{diaMes(d.fecha)}</span> · {d.bloqueado!.motivo}
                      {d.cerrado && <span className="apagado"> (ya estaba cerrado)</span>}
                    </span>
                    <button
                      type="button"
                      className="boton boton--chico boton--fantasma"
                      onClick={() => void c.liberar(d.bloqueado!.id, d.fecha)}
                    >
                      Liberar
                    </button>
                  </div>
                ))}
            </div>
          </section>
        )}

        {c.calendario.ultimaFechaUtil && (
          <p className="nota--fina">
            Último día en que se puede entrenar: {fechaLarga(c.calendario.ultimaFechaUtil)}.
          </p>
        )}
      </aside>
    </div>
  );
}

function Celda({ d, onSimular }: { d: DiaCalendario; onSimular: () => void }): ReactNode {
  const hecha = d.sesionReal?.estado === 'completada';
  const noDisponible = d.cerrado || d.bloqueado !== null;

  const fondo = hecha
    ? 'var(--agua-tenue)'
    : d.entrada
      ? 'var(--ambar-tenue)'
      : noDisponible
        ? 'var(--hierro-hundido)'
        : 'transparent';

  const borde = d.esHoy ? 'var(--ambar)' : 'var(--hierro-borde)';

  return (
    <button
      type="button"
      onClick={onSimular}
      disabled={noDisponible}
      title={
        d.bloqueado
          ? d.bloqueado.motivo
          : d.cerrado
            ? 'Gimnasio cerrado'
            : d.descanso
              ? 'Descanso por recuperación'
              : d.entrada?.nombre ?? 'Libre'
      }
      style={{
        background: fondo,
        border: `1px solid ${borde}`,
        borderRadius: 'var(--r)',
        padding: '6px 7px',
        minHeight: 74,
        textAlign: 'left',
        color: 'inherit',
        opacity: noDisponible ? 0.55 : 1,
        cursor: noDisponible ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <span className="lectura" style={{ fontSize: 13, fontWeight: 600 }}>
        {d.fecha.slice(8)}
      </span>

      {hecha ? (
        <span className="nota--fina" style={{ color: 'var(--agua)' }}>
          hecha
        </span>
      ) : d.entrada ? (
        <span className="nota--fina" style={{ color: 'var(--ambar)', lineHeight: 1.25 }}>
          {d.entrada.nombre.replace(/^Día (\d).*/, 'Día $1')}
          {d.entrada.conflicto && ' ⚠'}
        </span>
      ) : d.cerrado ? (
        <span className="nota--fina">cerrado</span>
      ) : d.bloqueado ? (
        <span className="nota--fina">{d.bloqueado.motivo.slice(0, 12)}</span>
      ) : d.descanso ? (
        <span className="nota--fina">descanso</span>
      ) : null}
    </button>
  );
}

function Leyenda({ color, texto, borde = false }: { color: string; texto: string; borde?: boolean }): ReactNode {
  return (
    <span className="fila nota--fina" style={{ gap: 6 }}>
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: 2,
          background: color,
          border: borde ? '1px solid var(--hierro-borde)' : 'none',
          display: 'inline-block',
        }}
      />
      {texto}
    </span>
  );
}
